/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { Fragment, useCallback, useState, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';

import {
	selectInlineCommentCompoundExperience,
	UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { getLogger } from '@confluence/logger';
import { CommentButtonRefetchQuery } from '@confluence/inline-comments-queries';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';

import type {
	ResolvedCommentNotificationResolveInlineCommentMutation as ResolvedCommentNotificationResolveInlineCommentMutation$data,
	ResolvedCommentNotificationResolveInlineCommentMutationVariables as ResolvedCommentNotificationResolveInlineCommentMutation$variables,
} from './__types__/ResolvedCommentNotificationResolveInlineCommentMutation';
import { ResolvedNotificationContainer } from './styled-components';
import type { InlineCommentsMode } from './inlineCommentsTypes';
import { CommentErrorMessage } from './CommentErrorMessage';
import { InlineCommentMode } from './enum/InlineCommentMode';
import { InlineCommentFramework } from './enum/InlineCommentFramework';

const ResolvedCommentNotificationResolveInlineCommentMutation = gql`
	mutation ResolvedCommentNotificationResolveInlineCommentMutation(
		$commentId: ID!
		$resolved: Boolean!
	) {
		resolveInlineComment(commentId: $commentId, resolved: $resolved, dangling: false) {
			status
			resolveProperties {
				resolved
				resolvedUser
				isDangling
				resolvedFriendlyDate
				resolvedTime
			}
		}
	}
`;
const i18n = defineMessages({
	resolvedCommentDescription: {
		id: 'inline-comments-common.resolved-comment-description',
		description: 'Message to be displayed when user Resolves a comment',
		defaultMessage: 'You can view all the comments from the More actions menu.',
	},
	resolvedCommentTitle: {
		id: 'inline-comments-common.resolved-comment-title',
		description: 'Title to be displayed when user Resolves a comment',
		defaultMessage: 'Comment Resolved',
	},
});

type ResolvedCommentNotificationProps = {
	commentId: string;
	reopenComment: (markerRef?: string) => void;
	pageId: string;
	inlineMarkerRef: string;
	mode: InlineCommentsMode;
};

const logger = getLogger('resolved-comment-notification');

export const ResolvedCommentNotification: FC<ResolvedCommentNotificationProps> = ({
	commentId,
	reopenComment,
	pageId,
	inlineMarkerRef,
	mode,
}) => {
	const refetchQueries =
		mode !== 'view-all' ? [{ query: CommentButtonRefetchQuery, variables: { pageId } }] : [];
	const [reopenInlineCommentFn] = useMutation<
		ResolvedCommentNotificationResolveInlineCommentMutation$data,
		ResolvedCommentNotificationResolveInlineCommentMutation$variables
	>(ResolvedCommentNotificationResolveInlineCommentMutation, {
		refetchQueries,
	});
	const [error, setError] = useState<Error | null>(null);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const isLivePage = useIsCurrentPageLive();

	const getCommentModeForAnalytics = useCallback(() => {
		return mode === InlineCommentMode.EDIT
			? isLivePage
				? InlineCommentMode.LIVE
				: InlineCommentMode.EDIT
			: InlineCommentMode.VIEW;
	}, [mode, isLivePage]);

	const unresolveComment = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			e.preventDefault();

			const mtnVariables = {
				variables: { commentId, resolved: false },
			};

			experienceTracker.start({
				name: UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
				id: commentId,
				attributes: {
					mode: getCommentModeForAnalytics(),
					framework: InlineCommentFramework.ANNOTATION_PROVIDER,
					annotationId: inlineMarkerRef,
				},
			});

			reopenInlineCommentFn({
				...mtnVariables,
			})
				.then(() => {
					reopenComment(inlineMarkerRef);

					experienceTracker.succeed({
						name: UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
					});
				})
				.catch((error) => {
					logger.error`An error occurred when reopening a comment ${error}`;

					setError(error);
					experienceTracker.stopOnError({
						name: UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
						error,
					});
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			commentId,
			reopenComment,
			reopenInlineCommentFn,
			inlineMarkerRef,
			experienceTracker,
			getCommentModeForAnalytics,
			mode,
		],
	);

	const firedAbortRef = React.useRef(false);
	if (firedAbortRef.current === false) {
		// On the initial render of this component we want to abort any in-progress select-inline-comment experience
		// as by rendering this component we are indicating that the comment has been resolved.
		selectInlineCommentCompoundExperience.abort({
			abortReason: 'Selected comment which has been resolved',
		});
		if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
			experienceTracker.abort({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				reason: 'Resolved comment was selected',
				attributes: {
					stopPoint: 'inline-comments-common/ResolvedCommentNotification',
				},
			});
		}
		firedAbortRef.current = true;
	}

	return (
		<Fragment>
			<ResolvedNotificationContainer>
				<CheckCircleIcon
					color={token('color.icon.success')}
					label=""
					spacing="spacious"
					testId="inline-comments.confirm-icon"
				/>
				<Heading size="xsmall">
					<FormattedMessage {...i18n.resolvedCommentTitle} />
				</Heading>
				<FormattedMessage {...i18n.resolvedCommentDescription} tagName="p" />
				{/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, @atlaskit/design-system/no-html-anchor */}
				<a onClick={unresolveComment}>
					<FormattedMessage
						id="inline-comments-common.undo-resolved-comment"
						defaultMessage="Undo resolved comment"
						description="Undo Resolved comment"
					/>
				</a>
			</ResolvedNotificationContainer>
			{error && <CommentErrorMessage error={error} action="reopen" />}
		</Fragment>
	);
};
